import { Image } from "./image";
import { BasicProperty, AllProperty } from "./property";
import { AgeVerification } from "./age-verification";
import { MasterData } from "./master-data";
import { IncomeProof } from "./income-proof";
import { AssetProof } from "./asset-proof";
import { ChanceTime } from "./chance-time";
import { Alert } from "./notice";
import { Question } from "./question";
import { Board } from "./board";

export enum Gender {
  Male = 1,
  Female = 2,
}

export const Campaign = {
  SwipeGoalNum: 30,
};

export const AgeVerificationStatus = {
  Pass: "pass",
  Reject: "reject",
  InReview: "inreview",
};

export enum StatusPath {
  LikeActive = "assets/img/tutorial/ic_like_cell_active.png",
  LikeInactive = "assets/img/tutorial/ic_like_cell_inactive.png",
  MsgBalloon = "assets/img/search/ic_search_message.png",
}

export enum DiscloseImg {
  to_public = 10, // 常に公開
  to_like = 20, // いいねしたお相手のみ公開
  to_message = 30, // メッセージしたお相手のみ公開
  to_favorite = 40, // お気に入りしたお相手のみ公開
  to_none = 90, // 常に非公開
}

export enum Distribution {
  ios = 1,
  android = 2,
  free = 3,
  web = 4,
}

export enum SubscriptionTypeID {
  Web_1M = 10008,
  Web_3M = 10007,
  Web_6M = 10006,
  Web_12M = 10005,
}

export const DISCLOSE_IMG_TO_PUBLIC = "to_public";
export const DISCLOSE_IMG_TO_MESSAGE = "to_message";

export const MALE_DEFAULT_IMG_PATH = "assets/img/default-user-image/male.png";
export const FEMALE_DEFAULT_IMG_PATH =
  "assets/img/default-user-image/female.png";

export class User {
  id: number;
  status: string;
  is_secret: boolean;
  is_freezed: boolean;
  is_excellent: boolean;
  gender: Gender;
  is_cast: boolean;
  age: number;
  created_at: Date;
  last_action_time: Date;
  property: BasicProperty;
  age_verfication: AgeVerification;
  income_proof: IncomeProof;
  asset_proof: AssetProof;
  top_image: Image;
  images: Image[];
  subImages: Image[];
  income: string;
  facebook_friend_count: number;
  today_entry: {
    person_num: number;
    areas: {
      id: number;
      name: string;
    }[];
  };
  type_of_registration: number;
  talked: boolean;
  matched: boolean;
  residence_location: string;
  statusPath?: StatusPath;
  // FIXME:
  birthday: Date = new Date();
  is_gyosha: boolean;
  meeting_entries: any;
  boards: Board[];
  locations: any;
  campaign_swipe_num: number;
  campaign: boolean;
  subscription_type_id: number;
  subscription_end_at = new Date();
  subscription_start_at = new Date();
  purchased_subscription_count: number;
  is_unsubscribed: boolean;
  good_count: number;
  good_num: any;
  has_chat_room_with_fw_75: boolean;
  reported_num: number;
  chat_room_id: number;
  met: boolean;
  is_warning: boolean;
  reported_category_count: any;
  good_num_within_a_week: number;
  in_time_subscription: {
    royal_subscription: {
      start_at: Date;
      end_at: Date;
      unrenewed_at: Date;
    };
    paid_subscription: {
      start_at: Date;
      end_at: Date;
    };
  };
  profile_movie: {
    mosaic: boolean;
    disclose: string;
    passed_movie_url: string;
    passed_thumbnail_url: string;
    aasm_state: string;
    id: number;
    movie_url: string;
    thumbnail_url: string;
  };
  required_point_to_like: number;
  alerts: Alert[];
  // Userではsearchとactivityのみ UserDetailではすべて
  questions?: Question[];
  relationshipId: number;
  reported?: boolean;

  constructor(fields: any) {
    for (const f in fields) {
      this[f] = fields[f];
    }
  }

  getCampaignSwipeNum(): number {
    return this.campaign_swipe_num;
  }

  getFacebookFriendCount(): number {
    return this.facebook_friend_count || 0;
  }

  getGender(): Gender {
    return this.gender;
  }

  getStatus(): string {
    return this.status;
  }

  getMeetingEntries(): any {
    return this.meeting_entries || [];
  }

  getTodayEntry(): {
    person_num: number;
    areas: { id: number; name: string }[];
  } {
    return this.today_entry;
  }

  getTodayAreas(): { id: number; name: string }[] {
    if (!this.getTodayEntry()) return;
    return this.getTodayEntry().areas;
  }

  getTodayAreaIds(): number[] {
    if (!this.getTodayEntry()) return;
    return this.getTodayAreas().map((area) => area.id);
  }

  getTodayAreaNames(): string[] {
    if (!this.getTodayAreas()) return;
    return this.getTodayAreas().map((area) => area.name);
  }

  getTodayPersonNum(): number {
    if (!this.getTodayEntry()) return;
    return this.getTodayEntry().person_num;
  }

  getUserId(): number {
    return this.id;
  }

  isMale(): boolean {
    return this.gender === Gender.Male;
  }

  isFemale(): boolean {
    return this.gender === Gender.Female;
  }

  isAdmin(): boolean {
    return this.getStatus() == "admin";
  }

  isPremium(): boolean {
    return this.getStatus() == "premium";
  }

  isRoyal(): boolean {
    if (this.in_time_subscription) {
      return !!this.in_time_subscription.royal_subscription;
    } else {
      return false;
    }
  }

  isNormal(): boolean {
    return this.getStatus() == "normal";
  }

  isFreezed(): boolean {
    return this.is_freezed;
  }

  isNewUser(): boolean {
    const now = new Date();
    return (
      (now.getTime() - this.getCreatedAt().getTime()) / 1000 / 60 / 60 / 24 <=
      30
    );
  }

  isSecret(): boolean {
    return this.is_secret;
  }

  isExcellent(): boolean {
    return this.is_excellent;
  }

  isCampaign(): boolean {
    return this.campaign;
  }

  isEvenId(): boolean {
    return this.id % 2 === 0;
  }

  isOddId(): boolean {
    return this.id % 2 === 1;
  }

  getLastLoginTimeType(): string {
    if (!this.last_action_time) return "offline";
    const now: Date = new Date();
    const date: Date = new Date(this.last_action_time);
    const sub: number = +now - +date;

    // less than 5 minutes
    if (sub < 60 * 5 * 1000) {
      return "online";
    }
    // less than 12 hours
    else if (sub < 60 * 60 * 12 * 1000) {
      for (let index = 1; index <= 12; index++) {
        if (sub < 60 * 60 * index * 1000) {
          return index.toString() + "-hour";
        }
      }
    }
    // less than 1 days
    else if (sub < 60 * 60 * 24 * 1000) {
      return "one-day";
    }
    // less than 3 days
    else if (sub < 60 * 60 * 24 * 3 * 1000) {
      return "these-days";
    } else {
      return "offline";
    }
  }
  getLastLoginTimeTypeDisplayStr(): string {
    const type = this.getLastLoginTimeType();
    if (type.endsWith("hour")) {
      return type.replace(/-hour/g, "時間以内");
    }
    switch (type) {
      case "online":
        return "オンライン";
      case "one-day":
        return "24時間以内";
      case "these-days":
        return "3日以内";
    }
  }

  getTopImageUrl(): string {
    if (this.isDeleted() || this.isUnsubscribed())
      return "assets/img/deleted-thumb.png";
    return this.top_image.image_url;
  }

  getTopImageUrlWithBlockOrHide(isMeGyosha: boolean): string {
    if (
      this.isDeleted() ||
      this.isUnsubscribed() ||
      (!isMeGyosha && this.isGyosha())
    )
      return "assets/img/deleted-thumb.png";
    return this.top_image.image_url;
  }

  setTopImageUrl(url: string): void {
    this.top_image.image_url = url;
  }

  getFaceWeight(): number {
    if (this.isDeleted() || !this.top_image) return 0;
    return this.top_image.face_weight;
  }

  isTopImage(): boolean {
    // バグで画像が1枚もない場合とデフォルト画像の場合
    if (!this.top_image) {
      return false;
    }
    return (
      this.top_image.image_url !==
      (MALE_DEFAULT_IMG_PATH || FEMALE_DEFAULT_IMG_PATH)
    );
  }

  getImages(): Image[] {
    return this.images.map((image) => {
      return new Image(image);
    });
  }

  getImageUrls(): string[] {
    return this.images.map((image) => image.image_url);
  }

  getTopImage(): Image {
    return new Image(this.top_image);
  }

  getTopImageBeforeReviewing(): boolean {
    if (!this.top_image) {
      return false;
    }
    return this.getTopImage().isBeforeReviewing();
  }

  getSubImages(): Image[] {
    this.subImages = [];
    for (let i = 1; i < this.getImages().length; i++) {
      this.subImages.push(this.getImages()[i]);
    }
    return this.subImages;
  }

  getImagesCount(): number {
    if (this.images) {
      if (this.isMale()) {
        return this.images.filter(
          (image) => image.image_url !== MALE_DEFAULT_IMG_PATH
        ).length;
      } else {
        return this.images.filter(
          (image) => image.image_url !== FEMALE_DEFAULT_IMG_PATH
        ).length;
      }
    } else {
      return 0;
    }
  }

  hasImages(): boolean {
    return this.getImagesCount() > 0;
  }

  hasAge(): boolean {
    return !!this.age;
  }

  getAge(): number {
    return this.age;
  }

  getAgeWithUnit(): string {
    return this.getAge() ? this.getAge().toString() + "歳" : "";
  }

  getNickname(): string {
    return this.property.nickname || "";
  }

  getNicknameWithAge(isMeGyosha: boolean): string {
    let str = this.getNickname();
    str =
      this.isUnsubscribed() ||
      this.isDeleted() ||
      (!isMeGyosha && this.isGyosha())
        ? "[退会済み]" + str
        : str;
    if (this.age) {
      // 歳はfont-weightが異なるため、html側で表示している
      str += "  " + this.age;
    }
    return str;
  }

  getNicknameWithBlockOrHide(isMeGyosha: boolean): string {
    if (
      this.isDeleted() ||
      this.isUnsubscribed() ||
      (!isMeGyosha && this.isGyosha())
    ) {
      return "[退会済み]" + this.getNickname();
    } else {
      return this.getNickname();
    }
  }

  getDisplayNameWithAgeWithBlockOrHide(
    isMeGyosha: boolean,
    displayName: string
  ): string {
    let str = !displayName ? this.getNickname() : displayName;
    str =
      this.isDeleted() ||
      this.isUnsubscribed() ||
      (!isMeGyosha && this.isGyosha())
        ? "[退会済み]" + str
        : str;
    if (this.age) {
      // 歳はfont-weightが異なるため、html側で表示している
      str += "  " + this.age;
    }
    return str;
  }

  getOneWord(): string {
    return this.property.one_word;
  }

  getOneWordOrSelfIntroduction(): string {
    return this.property.one_word || this.property.self_introduction;
  }

  getResidenceLocation(): string {
    return this.property.residence_location;
  }

  getRawOneWord(): string {
    return this.property.one_word;
  }

  getBusinessType(): string {
    return this.property.business_type || "";
  }

  getBusinessTypeWithAge(): string {
    let str = this.getBusinessType();
    if (this.age) {
      // 歳はfont-weightが異なるため、html側で表示している
      str += " " + this.age;
    }
    return str;
  }

  getBusinessTypeWithOneWord(): string {
    let str = this.getBusinessType();
    if (this.getOneWordOrSelfIntroduction()) {
      str += " / " + this.getOneWordOrSelfIntroduction();
    }
    return str;
  }

  getCreatedAt(): Date {
    return new Date(this.created_at);
  }

  getIncome(): string {
    return this.income || "秘密";
  }

  getIncomeOrBlank(): string {
    return this.income || "";
  }

  isDeleted(): boolean {
    return this.getStatus() == "deleted";
  }

  isIosPremium(): boolean {
    return this.type_of_registration == 1;
  }

  isWebPremium(): boolean {
    return this.type_of_registration == 2;
  }

  isWebPremiumStopped(): boolean {
    return this.type_of_registration == 3;
  }

  isAndroidPremium(): boolean {
    return this.type_of_registration == 4;
  }

  isTalked(): boolean {
    return this.talked;
  }

  setTalked(flag): void {
    this.talked = flag;
  }

  getBirthday(): Date {
    return this.birthday;
  }

  setBirthday(birthday: Date): void {
    this.birthday = birthday;
  }

  isGyosha(): boolean {
    return this.is_gyosha;
  }

  isWarning(): boolean {
    return this.is_warning;
  }

  getTopImageUrlWithoutGyosha(isMeGyosha: boolean): string {
    if (
      this.isUnsubscribed() ||
      this.isDeleted() ||
      (!isMeGyosha && this.isGyosha())
    )
      return "assets/img/deleted-thumb.png";
    return this.top_image.image_url;
  }

  isPassedIncomeProof(): boolean {
    if (this.isMale() && this.income_proof) {
      return this.income_proof.status == "pass";
    }
    return false;
  }

  isPassedAssetProof(): boolean {
    if (this.isMale() && this.asset_proof) {
      return this.asset_proof.status == "pass";
    }
    return false;
  }

  isMosaicImage(index: number): boolean {
    return this.images[index].is_mosaic;
  }

  getSubscriptionId(): number {
    return this.subscription_type_id;
  }

  getSubscriptionStartedAt(): Date {
    return new Date(this.subscription_start_at);
  }

  getSubscriptionEndAt(): Date {
    return this.subscription_end_at;
  }

  isUnsubscribed(): boolean {
    return this.is_unsubscribed;
  }

  getRoyalSubscriptionStartedAt(): Date | undefined {
    if (this.isRoyal()) {
      return this.in_time_subscription.royal_subscription.start_at;
    } else {
      return undefined;
    }
  }

  getDisplayedGoodCount(): string {
    if (this.good_num.value >= 100) {
      return "99+";
    } else if (100 > this.good_num.value && this.good_num.value > 10) {
      return this.good_num.value.toString();
    } else {
      return "~10";
    }
  }

  isHasChatRoomWithFw75(): boolean {
    return this.has_chat_room_with_fw_75;
  }

  getReportedNum(): number {
    return this.reported_num;
  }

  getBodyType(): string {
    return this.property.body_type;
  }

  getPurpose(): string {
    return this.property.purpose;
  }

  getSimpleProfile(): string {
    if (this.isFemale()) {
      let simpleProfile: string = "";
      if (this.getBusinessType()) {
        simpleProfile += this.getBusinessType();
        simpleProfile += "/";
      }
      if (this.getBodyType()) {
        simpleProfile += this.getBodyType();
        simpleProfile += "/";
      }
      if (this.getPurpose()) {
        simpleProfile += this.getPurpose();
        simpleProfile += "/";
      }

      return simpleProfile.slice(0, -1);
    }
    let simpleProfile: string = "";
    if (this.getBusinessType()) {
      simpleProfile += this.getBusinessType();
      simpleProfile += "/";
    }
    if (this.getIncome()) {
      simpleProfile += this.getIncome();
      simpleProfile += "/";
    }
    if (this.getPurpose()) {
      simpleProfile += this.getPurpose();
      simpleProfile += "/";
    }
    return simpleProfile.slice(0, -1);
  }

  getChatRoomId(): number {
    return this.chat_room_id;
  }

  getReportedCategoryCount(): any {
    return this.reported_category_count;
  }

  isRareLikeUser(): boolean {
    return this.good_num_within_a_week <= 0;
  }

  isVideoMosaic(): boolean {
    return this.profile_movie.mosaic;
  }

  getVideoUrl(): string {
    return this.profile_movie ? this.profile_movie.passed_movie_url : "";
  }

  getThumbnailUrl(): string {
    return this.profile_movie.passed_thumbnail_url;
  }

  hasValidQuestions(): boolean {
    if (!this.questions) return false;
    return (
      this.questions.filter(
        (question) => question.content && question.other_text !== ""
      ).length > 0
    );
  }

  getBoards(): Board[] {
    this.boards = this.boards
      ? this.boards.map((board) => new Board(board))
      : [];
    return this.boards;
  }

  getTodayBoards(): Board[] {
    let today: string = new Date().toISOString();
    // NOTE "YYYY-MM-ddThh:mm:ssZ" => "YYYY-MM-dd"に整形
    today = today.substring(0, today.indexOf("T"));

    let todayBoard: Board[] = [];

    this.getBoards().forEach((board: Board) => {
      if (board.reserved_date === today) {
        todayBoard.push(board);
      }
    });
    return todayBoard;
  }
}

export class UserDetail extends User {
  property: AllProperty;
  age_verfication: AgeVerification;
  income_proof: IncomeProof;
  asset_proof: AssetProof;
  constructor(fields: any) {
    super(fields);
  }

  getProperty(): AllProperty {
    return this.property;
  }
  getSelfIntroduction(): string {
    return this.property.self_introduction;
  }
  getFutureDream(): string {
    if (!this.property.future_dream) {
      return "";
    }
    return this.property.future_dream;
  }
  isTalked(): boolean {
    return this.talked;
  }
  setTalked(flag): void {
    this.talked = flag;
  }
  getResidenceLocation(): string {
    return this.property.residence_location;
  }
  getHeightAndBodyType(): string {
    if (this.property.height && this.property.body_type) {
      return this.property.height + "cm / " + this.property.body_type;
    } else if (this.property.height) {
      return this.property.height + "cm";
    } else {
      return this.property.body_type;
    }
  }
  getIncomeType(): string {
    return this.property.income_type;
  }
  getBusinessTypeAndOneWord(): string {
    if (this.property.business_type && this.property.one_word) {
      return this.property.business_type + " / " + this.property.one_word;
    } else if (this.property.business_type) {
      return this.property.business_type;
    } else if (this.property.one_word) {
      return this.property.one_word;
    } else {
      return "";
    }
  }

  getFirstAlertTitle(): string {
    if (this.alerts[0]) {
      return this.alerts[0].title;
    }
  }

  hasRichAlertInFirst(): boolean {
    if (!this.alerts) {
      return false;
    }
    if (this.alerts[0] && this.alerts[0].category === "rich_like") {
      return true;
    } else {
      false;
    }
  }

  getLooksString(): string {
    if (this.property.looks.length > 1) {
      let looksStringList: string[] = JSON.parse(this.property.looks).map(
        (look) => MasterData.getData()["lookses"][look]
      );
      return looksStringList.join("、");
    } else {
      return "";
    }
  }

  getQuestions(): Question[] {
    return this.questions;
  }

  getValidQuestions(): Question[] {
    return this.questions.filter(
      (question) => question.content && question.other_text !== ""
    );
  }

  getBoards(): Board[] {
    this.boards = this.boards
      ? this.boards.map((board) => new Board(board))
      : [];
    return this.boards;
  }

  getTodayBoards(): Board[] {
    let today: string = new Date().toISOString();
    // NOTE "YYYY-MM-ddThh:mm:ssZ" => "YYYY-MM-dd"に整形
    today = today.substring(0, today.indexOf("T"));

    let todayBoard: Board[] = [];

    this.getBoards().forEach((board: Board) => {
      if (board.reserved_date === today) {
        todayBoard.push(board);
      }
    });
    return todayBoard;
  }

  hasValidQuestions(): boolean {
    return (
      this.questions.filter(
        (question) => question.content && question.other_text !== ""
      ).length > 0
    );
  }
}

export class Me extends UserDetail {
  is_secret: boolean;
  is_excellent: boolean;
  meeting_entry_num: number;
  property: AllProperty;
  age_verification: AgeVerification;
  income_proof: IncomeProof;
  asset_proof: AssetProof;
  invite_code: string;
  already_input_invite_code: boolean;
  email: string;
  points: {
    basic: {
      current_amount: number;
    };
    rich_like: {
      in_time_amount: number;
    };
  };
  in_time_subscription: {
    royal_subscription: {
      start_at: Date;
      end_at: Date;
      unrenewed_at: Date;
    };
    paid_subscription: {
      term_start_at: Date;
      start_at: Date;
      end_at: Date;
    };
    in_time_premium_subscription: {
      term_start_at: Date;
      start_at: Date;
      end_at: Date;
      unrenewed_at: Date;
    };
  };
  chance_times: ChanceTime[];
  search_shown: number;
  like_cvr: number;
  state: string;
  continuing_month_count: number;
  next_subscription_date: Date;
  questions: Question[];
  latest_dated_survey: {
    created_at: Date;
  };
  valid_quality_checks_id: number[];

  constructor(fields: any) {
    super(fields);
  }

  propertyInputRate(): number {
    const basicInformationColumn: {
      name: string;
      displayName: string;
    }[] = MasterData.getBasicInformationColumnArray().filter((column) => {
      if (this.isFemale() && column.onlyMale) return false;
      if (this.isMale() && column.onlyFemale) return false;
      if (!this.isAdmin() && column.onlyAdmin) return false;
      return true;
    });
    let allColumn = ["self_introduction", "one_word"];
    if (this.isFemale()) {
      allColumn.push("future_dream");
    }
    allColumn = allColumn.concat(basicInformationColumn.map((c) => c.name));

    const property = this.getProperty();
    let inputNum: number = 0;
    let inputRate: number;
    allColumn.forEach((column: string) => {
      if (
        property[column] !== undefined &&
        property[column] !== null &&
        property[column] !== "" &&
        property[column].length !== 0 &&
        property[column] !== "[]"
      ) {
        inputNum++;
      }
    });

    inputRate = Math.round((inputNum / allColumn.length) * 100);

    if (inputRate > 100) {
      inputRate = 100;
    }

    return Math.round((inputNum / allColumn.length) * 100);
  }

  getEmail(): string {
    return this.email || "";
  }

  hasTermStartAt(): boolean {
    if (!this.in_time_subscription) {
      return false;
    }
    if (this.in_time_subscription.paid_subscription) {
      return !!this.in_time_subscription.paid_subscription.term_start_at;
    } else {
      return false;
    }
  }

  getContinuingMonthCount(): number {
    return this.continuing_month_count;
  }

  getNextSubscriptionDate(): Date {
    return new Date(this.next_subscription_date);
  }

  getTermStartAt(): Date {
    return new Date(this.in_time_subscription.paid_subscription.term_start_at);
  }

  isPassedAgeVerification(): boolean {
    return this.age_verification.status == AgeVerificationStatus.Pass;
  }

  isInreviewAgeVerification(): boolean {
    return this.age_verification.status == AgeVerificationStatus.InReview;
  }

  isCampaignDone(): boolean {
    return (
      this.campaign_swipe_num >= Campaign.SwipeGoalNum &&
      this.age_verification.status == AgeVerificationStatus.Pass
    );
  }

  getIncomeProofStatus(): string {
    if (this.isMale() && this.income_proof) {
      return this.income_proof.status;
    }
    return "";
  }

  getAssetProofStatus(): string {
    if (this.isMale() && this.asset_proof) {
      return this.asset_proof.status;
    }
    return "";
  }

  // TODO: 年確タイミング変更に伴い、要コメント
  canChat(): boolean {
    if (this.isFemale()) {
      return this.isPassedAgeVerification();
    } else {
      return (
        (this.isPassedAgeVerification() && this.isPremium()) || this.isAdmin()
      );
    }
  }

  getInviteCode(): string {
    return this.invite_code;
  }

  getUserCode(): string {
    // FIXME: ユーザー一意のコードとして今invite_codeを利用している
    return this.getInviteCode();
  }

  setAlreadyInputInviteCode(flag: boolean): void {
    this.already_input_invite_code = flag;
  }

  isAlreadyInputInviteCode(): boolean {
    return !!this.already_input_invite_code;
  }

  getMeetingEntryNum(): number {
    return this.meeting_entry_num;
  }

  getUserChanceTimeFromIndex(chanceTimeIndex: number): ChanceTime {
    return this.chance_times[chanceTimeIndex];
  }

  getCurrentRunningLikeChanceTime(): ChanceTime {
    let currentRunningChanceTime: ChanceTime = this.chance_times.filter(
      (chance_time) => {
        return (
          chance_time.state === "running" && chance_time.category === "like"
        );
      }
    )[0];
    currentRunningChanceTime = new ChanceTime(currentRunningChanceTime);
    return currentRunningChanceTime;
  }

  //current_running_chance_time が chance_time 配列の何番目かを取得
  getCurrentRunningLikeChanceTimeIndex(): number {
    let chanceTimeIndex: number;
    for (let index = 0; index < this.chance_times.length; index++) {
      if (
        this.chance_times[index].state === "running" &&
        this.chance_times[index].category === "like"
      ) {
        chanceTimeIndex = index;
        break;
      }
    }
    return chanceTimeIndex;
  }

  getFirstNotFiredLikeChanceTime(): ChanceTime {
    let notFiredChanceTime: ChanceTime = this.chance_times.filter(
      (chance_time) => {
        return (
          chance_time.state === "not_fired" && chance_time.category === "like"
        );
      }
    )[0];
    if (notFiredChanceTime) {
      notFiredChanceTime = new ChanceTime(notFiredChanceTime);
    }

    return notFiredChanceTime;
  }

  hasRunningLikeChanceTime(): boolean {
    let runningChanceTimes: ChanceTime[] = this.chance_times.filter(
      (chance_time) => {
        return (
          chance_time.state === "running" && chance_time.category === "like"
        );
      }
    );
    return runningChanceTimes.length > 0;
  }

  hasNotFiredLikeChanceTime(): boolean {
    let notFiredChanceTimes: ChanceTime[] = this.chance_times.filter(
      (chance_time) => {
        return (
          chance_time.state === "not_fired" && chance_time.category === "like"
        );
      }
    );
    return notFiredChanceTimes.length > 0;
  }

  isWithinThreeDaysFromSignUp(): boolean {
    let threeDaysAgo: Date = new Date();
    threeDaysAgo.setDate(new Date().getDate() - 3);
    let signUpDate: Date = this.getCreatedAt();
    // 新規登録から3日以内の場合
    if (signUpDate.getTime() > threeDaysAgo.getTime()) {
      return true;
    } else {
      return false;
    }
  }

  getLikeCvr(): number {
    return this.like_cvr;
  }

  getSearchShown(): number {
    return this.search_shown;
  }

  hasRichLikePoints(): boolean {
    return this.points.rich_like.in_time_amount > 0;
  }

  getCurrentPoint(): number {
    return this.points.basic.current_amount;
  }

  getCurrentRichLikePoint(): number {
    return this.points.rich_like.in_time_amount;
  }

  isLikeCvrOver6AndSearchShownOver100(): boolean {
    return this.getLikeCvr() >= 0.06 && this.getSearchShown() >= 100;
  }

  isPhoneRequired(): boolean {
    if (this.state === "phone_required") {
      return true;
    } else {
      return false;
    }
  }

  getVideoId(): number {
    return this.profile_movie.id;
  }

  isVideoBeforeReviewing(): boolean {
    if (this.profile_movie.aasm_state === "before_reviewing") {
      return true;
    } else {
      return false;
    }
  }

  isVideoPublic(): boolean {
    if (this.profile_movie.disclose === "public") {
      return true;
    } else {
      return false;
    }
  }

  getVideoUrl(): string {
    return this.profile_movie.movie_url;
  }

  getThumbnailUrl(): string {
    return this.profile_movie.thumbnail_url;
  }

  isRoyalSubscriptionStopped(): boolean {
    if (this.isRoyal()) {
      return !!this.in_time_subscription.royal_subscription.unrenewed_at;
    } else {
      return false;
    }
  }

  getUnrenewedAt(): Date {
    if (this.in_time_subscription) {
      return this.in_time_subscription.in_time_premium_subscription
        .unrenewed_at;
    } else {
      return null;
    }
  }

  getQuestions(): Question[] {
    return this.questions;
  }

  hasLatestDatedSurvey(): boolean {
    return !!this.latest_dated_survey;
  }

  getLatestDatedSurvey(): Date {
    return new Date(this.latest_dated_survey.created_at);
  }

  getValidQualityCheckIds(): number[] {
    return this.valid_quality_checks_id || [];
  }

  getPurchasedSubscriptionCount(): number {
    return this.purchased_subscription_count;
  }
}



// WEBPACK FOOTER //
// ./src/models/user.ts